// src/hooks/usePwaInstallPrompt.js
// Simple hook to surface the browser's PWA install prompt (where supported).
// It listens for the `beforeinstallprompt` event, exposes a boolean flag to
// show an "Install app" UI, and provides a function to trigger the prompt.

import { useEffect, useState } from 'react';

export const usePwaInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      // Prevent the browser's mini-infobar from appearing.
      event.preventDefault();
      setDeferredPrompt(event);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      // Once installed, no need to keep showing the install UI.
      setDeferredPrompt(null);
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return null;

    try {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setCanInstall(false);
      return choiceResult;
    } catch (error) {
      // If something goes wrong, just clear state and surface the error to caller.
      setDeferredPrompt(null);
      setCanInstall(false);
      throw error;
    }
  };

  return { canInstall, promptInstall };
};

export default usePwaInstallPrompt;

